/* Variant-picker hover/touch preload debounce, 2026-08-19.

   Finding: theme.js's own stock preload feature (onOptionPreload_fn, wired
   to pointerenter/touchstart on every unchecked option label via the
   ftlabs Delegate library on document.body) calls renderForCombination_fn
   on every single hover, with no debounce. Sweeping a cursor across a row
   of swatches fires one full-section-render fetch per swatch touched
   (confirmed: 5-8 concurrent ~200KB fetches from one hover/click sequence
   on Metal Wallet, each carrying the product's whole gallery markup) -
   most of them for combinations the customer has already moved past by
   the time the response lands, which competes for bandwidth with the one
   response that actually matters and makes the eventual click slower, not
   faster.

   Why this is a debounce, not an abort-in-flight: renderForCombination_fn
   caches the fetch's promise in VariantPicker's own private static field
   (_preloadedHtml, keyed by option-values+section-id), and a real click on
   an already-preloading combination awaits that exact cached promise. That
   field is a true JS class-private static, unreachable from here - there
   is no way for an external script to invalidate a specific cache entry.
   Aborting an in-flight fetch would make its cached promise reject, and if
   the customer later clicked back onto that exact combination, the click
   would await a rejected promise and the variant change would throw
   instead of rendering. So instead of starting wasteful fetches and
   racing to cancel them, this stops them from starting at all: a
   pointerenter/touchstart on a swatch is held for a short settle window,
   and only the one still-hovered target after that window is allowed
   through to theme.js's own handler, unchanged. A real click's own
   'change' event is never touched by this file, so it is never delayed,
   never debounced, never at risk from this change.

   Correction, 2026-08-28: this section originally scoped
   onIntersection_fn's own bulk pre-render as a one-time, page-load-only
   cost and left it untouched here. That was wrong, and it is fixed
   separately in assets/ascot-preload-guard.js, NOT in this file - that fix
   has to run and install before theme.js executes at all (the initial
   page-load <variant-picker> is upgraded, and its IntersectionObserver
   created, synchronously inside theme.js's own customElements.define()
   call, before any deferred script - including this one - has run), so it
   cannot live in this file, which is deliberately kept deferred, after
   theme.js, matching the rest of this theme's override pattern. See that
   file's own header for the finding and the fix.

   Override pattern: reads no private state, calls nothing on VariantPicker,
   never touches theme.js. Works purely by intercepting pointerenter and
   touchstart in the capture phase on document, which - because DOM event
   capturing always runs document before document.body regardless of
   registration order - fires before the Delegate instance theme.js
   registers on document.body ever sees the event.
*/
(function () {
  var SWATCH_SELECTOR = 'input[data-option-position]:not(:checked) + label';
  var DEBOUNCE_MS = 120;
  var pendingByPicker = new WeakMap();

  function isStillUnselected(target) {
    var input = target.previousElementSibling;
    return input && input.matches('input[data-option-position]') && !input.checked;
  }

  function settle(picker, target) {
    var existing = pendingByPicker.get(picker);
    if (existing) clearTimeout(existing.timer);
    var timer = setTimeout(function () {
      pendingByPicker.delete(picker);
      if (!target.isConnected || !isStillUnselected(target)) return;
      var evt = new Event('pointerenter', { bubbles: false, cancelable: false, composed: true });
      evt.__ascotResettled = true;
      target.dispatchEvent(evt);
    }, DEBOUNCE_MS);
    pendingByPicker.set(picker, { timer: timer, target: target });
  }

  function onCapture(event) {
    if (event.__ascotResettled) return;
    var target = event.target.closest ? event.target.closest(SWATCH_SELECTOR) : null;
    if (!target) return;
    var picker = target.closest('variant-picker');
    if (!picker) return;
    event.ftLabsDelegateIgnore = true;
    settle(picker, target);
  }

  document.addEventListener('pointerenter', onCapture, true);
  document.addEventListener('touchstart', onCapture, true);
})();
